import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  Cart,
  MedicalConsultation,
  MedicalTest,
  CartQuantityItem,
  User,
  Drug,
} from '../../models';
import {
  AuthenticationService,
  DataService,
  UtilsService,
  PaymentService
} from '../../services';
import Swal from 'sweetalert2';
import { first } from 'rxjs/operators';
import { CountryISO } from 'ngx-intl-tel-input';

@Component({
  selector: 'app-add-order',
  templateUrl: './add-order.component.html',
  styleUrls: ['./add-order.component.scss']
})
export class AddOrderComponent implements OnInit {
  addOrderForm: FormGroup;
  paymentForm: FormGroup;
  submitted = false;
  user: User;
  cartItems: MedicalTest[] = [];
  separateDialCode = false;
  userCart: Cart;
  locations: string[];
  disableDate: string;
  orderToSave: [];
  countryISO = CountryISO;
  countryCodes = [
    { name: 'Uganda', code: 'UG' },
    { name: 'Kenya', code: 'KE' },
    { name: 'Tanzania', code: 'TZ' },
    { name: 'Rwanda', code: 'RW' },
  ];
  addConsultation: boolean;
  productQuantities: CartQuantityItem[] = [];
  productQuantitiesArray: number[];
  setTimeValue: string = '';
  totalCartCost: number;
  consultationAmount: number;
  cartItemName: { [id: number]: string } = {};
  isDrug: boolean;

  constructor (
    private formBuilder: FormBuilder,
    private authenticationService: AuthenticationService,
    private dataService: DataService,
    private utilsService: UtilsService,
    private paymentService: PaymentService
    ) { }

  ngOnInit(): void {
    this.user = this.authenticationService.currentUser()['user'];
    this.locations = this.dataService.getLocalLocationLabels();
    this.dataService.getLocalConsultationStatus().subscribe(status => {
      this.addConsultation = status
      if (this.addConsultation) {
        this.dataService.getLocalMedicalConsultationAmount().subscribe(amount => 
          this.consultationAmount = Number(amount) || 0);
      }
      this.cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
      this.cartItems.map(item => this.setItemName(item));
      this.isDrug = this.cartItems.length > 0 && 'country_of_origin' in this.cartItems[0];
      const quantities = JSON.parse(localStorage.getItem('quantities') || '[]') as number[];
      this.productQuantities = quantities.map(quantity => ({ quantity } as CartQuantityItem));
      this.totalCartCost = JSON.parse(localStorage.getItem('totalCartCost') || '0');
    });
    this.addOrderForm = this.formBuilder.group({
      clientName: [this.user?.firstname + ' ' + this.user?.lastname],
      email: [this.user?.email],
      telephone: [this.user?.telephone, Validators.required],
      birthDate: [this.user?.birth_date],
      availability: ['', Validators.required],
      district: ['', Validators.required],
      location: ['', Validators.required],
      note: [''],
      termsAndConditions: ['', Validators.required]
    });
    this.paymentForm = this.formBuilder.group({
      phone_number: ['', Validators.required],
      country: ['Uganda', Validators.required],
      city: ['', Validators.required],
      line_1: ['', Validators.required],
    });
    this.disableDate = this.utilsService.getMinDate();
    this.addOrderForm.statusChanges.subscribe(
      status => {
        if (status === 'VALID') {
          this.saveOrderInformation();
        }
      })
  }

  get f() {
    return this.addOrderForm.controls;
  }

  setItemName(item: MedicalTest | Drug): void {
    const name = this.dataService.getItemName(item);
    this.cartItemName[item.id] = name;
  }

  convertTo12HourFormat(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    if (value) {
      const timePart = value.split('T')[1];
      const [hours, minutes] = timePart.split(':').map(Number);
      if (hours >= 0 && hours <= 23) {
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const convertedHours = hours % 12 || 12;
        const formattedTime = `${this.padZero(convertedHours)}:${this.padZero(minutes)} ${ampm}`;
        const datePart = value.split('T')[0];
        this.setTimeValue = `${datePart} ${formattedTime}`;
      }
    }
  }

  padZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  submitOrder() {
    if (this.addOrderForm.invalid) {
      this.submitted = true;
      return;
    }
    const selectedCountry = this.paymentForm.controls.country.value;
    const countryCode = this.countryCodes.find(country => country.name === selectedCountry)?.code;
    const paymentDetails = {
      email: this.f.email.value,
      telephone: this.paymentForm.controls.phone_number.value,
      country_code: countryCode,
      firstname: this.user?.firstname,
      lastname: this.user?.lastname,
      line_1: this.paymentForm.controls.line_1.value,
      city: this.paymentForm.controls.city.value,
      price: this.totalCartCost,
    };
    this.paymentService.submitPayment(paymentDetails).pipe(first())
      .subscribe(response => {
        if ((response as any)?.code === 500) {
          Swal.fire({
            title: 'An error occurred',
            html: 'Payment not successful, please try again later or contact: <b>info@go-check.com</b>',
            icon: 'error',
            confirmButtonColor: '#f37b7b',
          });
          return;
        }
        const redirectUrl = (response as any)?.message?.redirect_url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      });
  }

  saveOrderInformation() {
    const plainFormData = {
      ...this.addOrderForm.value,
      userId: this.user?.id,
    };
    if (this.addConsultation && this.cartItems.length === 0) {
      plainFormData.availability = this.setTimeValue
    } else {
      plainFormData.availability = this.addOrderForm.value.availability;
    }
    if (this.f.district?.value && this.f.location?.value) {
      plainFormData.location = `${this.f.district.value} ${this.f.location.value}`;
    }
    this.dataService.saveToLocalStorage('orderData', plainFormData);
    this.dataService.saveToLocalStorage('consultation', this.addConsultation);
  }

}
